import { JSONSchema7 } from 'json-schema';
import { useMemo } from 'react';
import {
  createDefinitionSchema,
  createPanelDefinitionSchema,
  PanelDefinition,
  SupportedDataType,
  UnknownSpec,
  unknownDefinitionSchema,
} from '@/core';
import { PanelPlugin, PluginMetadata } from '../model';
import { usePlugin } from './plugin-registry';

/**
 * Helper function to get the Panel plugin schema.
 * This schema is the Zod schema that defines the panel options.
 */
export function usePanelPluginSchema(kind: string) {
  const { data: plugin, isLoading, error } = usePlugin('Panel', kind);
  return {
    schema: plugin?.schema,
    isLoading,
    error,
  };
}

/**
 * Helper function to get the Panel plugin JSON schema derived from the Zod schema.
 * This hook is useful for creating a JSON editor for the panel options.
 */
export function usePanelPluginJSONSchema(kind: string): JSONSchema7 | undefined {
  const { schema } = usePanelPluginSchema(kind);
  return useMemo(() => {
    if (!schema) {
      return undefined;
    }
    // TODO: Commenting out while we upgrade to Zod 4;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const panelSchema = createPanelDefinitionSchema(createDefinitionSchema(kind, schema), unknownDefinitionSchema);
    // return zodToJsonSchema(panelSchema) as JSONSchema7;
    return undefined;
  }, [kind, schema]);
}

/**
 * Gets the panel plugin and its supported data type based on its definition.
 */
export function usePanelPluginByDefinition(panelDefinition: PanelDefinition) {
  const panelKind = panelDefinition.spec.plugin.kind;
  const { data: plugin, isLoading, error } = usePlugin('Panel', panelKind);
  const dataType = getSupportedDataType(panelDefinition.spec.plugin.spec, plugin);
  return { plugin, dataType, isLoading, error };
}

/**
 * Returns the panel plugin's supported data type
 */
export function getSupportedDataType(pluginSpec: UnknownSpec, plugin?: PanelPlugin) {
  let supportedDataType = plugin?.supportedDataType ?? 'TimeSeries';

  if (Array.isArray(supportedDataType)) {
    // if panel plugin supports more than one data type, call mapSpecToDataType
    if (supportedDataType.length > 1) {
      if (!plugin?.mapSpecToDataType) {
        throw new Error('Unable to determine the supported data type of the panel.');
      }
      supportedDataType = plugin.mapSpecToDataType(pluginSpec);
    } else {
      supportedDataType = supportedDataType[0] as SupportedDataType;
    }
  }
  return supportedDataType;
}

/**
 * Determines whether a plugin supports the valid data type
 * It currently only works for data query plugins because supportedDataTypes is defined exclusively in the metadata for those plugins.
 */
export const doesPluginSupportDataType = ({
  validDataType,
  plugin,
}: {
  // getPlugins and getPluginMetadata are all async so these values are undefined when they are loading
  validDataType?: SupportedDataType;
  plugin?: PluginMetadata;
}) => {
  if (validDataType === undefined) {
    return true;
  }

  const metadata = plugin?.metadata;

  // If plugin does not have supportedDataType defined, assume plugin does not support the valid data type
  if (metadata?.supportedDataTypes === undefined) {
    return false;
  }

  if (Array.isArray(metadata.supportedDataTypes)) {
    return metadata.supportedDataTypes.includes(validDataType);
  }

  return metadata.supportedDataTypes === validDataType;
};
